/* eslint-disable camelcase*/
import { fromJS } from 'immutable';
import { createStructuredSelector } from 'reselect';
import { StatusCodes } from 'http-status-codes';

import logger from 'logger';
import ApiService from 'services/ApiService/ApiService';
import ApiException from 'services/ApiService/ApiException';
import parseCatch from 'utils/parseCatch';
import makeAction from 'utils/makeAction';

import {
  ADD_COMMENT_URL,
  SET_COMMENTS_URL,
  DELETE_COMMENT_URL,
  LIBRARY_VIDEOS_URL,
  GET_PERFORMER_VIDEOS,
} from './Api.js';


const ADD_COMMENT = 'cam4/profile/ADD_COMMENT';
const DELETE_COMMENT = 'cam4/profile/DELETE_COMMENT';
const SET_COMMENTS = 'cam4/profile/SET_COMMENTS';
const SET_LIBRARY_VIDEOS = 'cam4/profile/SET_LIBRARY_VIDEOS';
const SET_SHOP_VIDEOS = 'cam4/profile/SET_SHOP_VIDEOS';

const initialState = fromJS({
  libraryVideos: fromJS({}),
  shopVideos: fromJS({}),
});

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_SHOP_VIDEOS:
      return state.set('shopVideos', fromJS(action.shopVideos));
    case SET_LIBRARY_VIDEOS:
      return state.set('libraryVideos', fromJS(action.libraryVideos));
    case SET_COMMENTS:
      return state.set('comments', action.comments);
    case ADD_COMMENT:
      return state.set('comments', [action.comment].concat(state.get('comments')));
    case DELETE_COMMENT:
      return state.set(
        'comments',
        state.get('comments').filter((comm) => comm.commentId !== action.comment.commentId)
      );
    default:
      return state;
  }
}

export const NAME = 'profileBody';

// Input Selectors
const shopVideos = (state) => state[NAME].get('shopVideos');
const libraryVideos = (state) => state[NAME].get('libraryVideos');
const comments = (state) => state[NAME].get('comments');

/**
 * Actions
 */
const setShopVideos = makeAction(SET_SHOP_VIDEOS, 'shopVideos');
const setLibraryVideos = makeAction(SET_LIBRARY_VIDEOS, 'libraryVideos');
const setComments = makeAction(SET_COMMENTS, 'comments');
const addComment = makeAction(ADD_COMMENT, 'comment');
const removeComment = makeAction(DELETE_COMMENT, 'comment');

/**
 * API
 */
function getShopVideos(username, limit) {
  const params = {
    userName: username,
    sortBy: 'priority',
    status: 'APPROVED',
  };

  if (limit) {
    params.limit = limit;
  }

  return async(dispatch) => {
    let result = undefined;
    try {
      const response = await ApiService.get(GET_PERFORMER_VIDEOS, { params });
      result = dispatch(setShopVideos(response.data));
    } catch (err) {
      /**
       * We have access to custom error context
       */
      if (err instanceof ApiException) {
        logger.warn({ msg: err.message, url: err.url, status: err.status });
        parseCatch(err);
      }
    }

    return result;
  };
}

function getLibraryVideos(username, filterValue) {
  const params = { sortBy: 'date' };

  if (filterValue) {
    params.performer = filterValue;
  }

  return async(dispatch) => {
    let result = null;
    const url = ApiService.substitute(LIBRARY_VIDEOS_URL, { username });
    try {
      const response = await ApiService.get(url, params);

      if ([StatusCodes.OK, StatusCodes.NO_CONTENT].includes(response.status)) {
        result = dispatch(setLibraryVideos(response.data));
      }
    } catch (err) {
      /**
       * We have access to custom error context
       */
      if (err instanceof ApiException) {
        logger.warn({ msg: err.message, url: err.url, status: err.status });
        parseCatch(err);
      }
    }

    return result;
  };
}

const getComments = (username, offset) => async(dispatch) => {
  const url = ApiService.substitute(SET_COMMENTS_URL, { username, offset });

  try {
    const res = await ApiService.get(url);
    dispatch(setComments(res.data));
  } catch (err) {
    logger.error(err);
  }
};

const submitComment = (comment, username) => async(dispatch) => {
  const url = ApiService.substitute(ADD_COMMENT_URL, { username });

  const res = await ApiService.post(url, { data: comment });
  dispatch(addComment(res.data));
};

const deleteComment = (username, data) => async(dispatch) => {
  const url = ApiService.substitute(DELETE_COMMENT_URL, { username });

  try {
    await ApiService.put(url, { data });
    dispatch(removeComment(data));
  } catch (err) {
    logger.error(err);
  }
};

export const selector = createStructuredSelector({
  shopVideos,
  libraryVideos,
  comments,
});

export const actionCreators = {
  getComments,
  submitComment,
  deleteComment,
  getShopVideos,
  getLibraryVideos,
};
